import type { UniqueIdentifier } from '@dnd-kit/core';
import { useSortable } from '@dnd-kit/sortable';
import { Item2 } from 'components/DragAndDrop/components/Item2';
import { ECreateQuestionMode } from 'components/Questionsv2/builder/context/create-question.context';
import type { CSSProperties, ReactElement, ReactNode } from 'react';
import { useEffect, useState } from 'react';
import { EQuestionType } from '@hubertai/screening-question-types';
import type { TSortableItemBase } from '..';
import { useAppSelector } from 'rtk/query/hooks';
import { selectQuestionMode } from 'rtk/questionEditorSelector';
import { useQuestionEditorContext } from 'components/Questionsv2/builder/context/create-question.context';

function useMountStatus() {
  const [isMounted, setIsMounted] = useState(false);
  useEffect(() => {
    const timeout = setTimeout(() => setIsMounted(true), 500);
    return () => clearTimeout(timeout);
  }, []);
  return isMounted;
}

interface SortableItemProps {
  containerId: string | UniqueIdentifier;
  id: string | UniqueIdentifier;
  item: TSortableItemBase;
  index: number;
  itemContent?: ReactNode;

  handle: boolean;
  disabled?: boolean;
  style(args: unknown): CSSProperties;
  getIndex(id: UniqueIdentifier): number;
  renderItem(): ReactElement;
  wrapperStyle({ index }: { index: number }): CSSProperties;
}

const SortableItem = ({
  containerId,
  id,
  item,
  index,
  itemContent,
  handle,
  renderItem,
  style,
  getIndex,
  wrapperStyle,
  disabled: disabledProp,
}: SortableItemProps) => {
  const { questionId } = useQuestionEditorContext();
  const mode = useAppSelector((state) => selectQuestionMode(state, questionId));
  const type = useAppSelector((state) => state.questionEditor.byId[questionId].type);
  const isPublishedOrNonExclusiveQuestion = useAppSelector(
    (state) => state.questionEditor.byId[questionId].isPublishedOrNonExclusiveQuestion,
  );

  const disabled =
    disabledProp ||
    (mode === ECreateQuestionMode.InTemplateEdit &&
      containerId === 'text' &&
      type !== EQuestionType.Info &&
      (isPublishedOrNonExclusiveQuestion ?? true));
  const { setNodeRef, setActivatorNodeRef, listeners, isDragging, isSorting, over, overIndex, transform, transition } =
    useSortable({
      data: {
        containerId,
      },
      id,
      disabled,
    });
  const mounted = useMountStatus();
  const mountedWhileDragging = isDragging && !mounted;

  if (!item) {
    <div>Item is missing</div>;
  }

  return (
    <Item2
      ref={disabled ? undefined : setNodeRef}
      itemContent={itemContent}
      disabled={disabled}
      dragging={isDragging}
      sorting={isSorting}
      handle={handle}
      handleProps={handle ? { ref: setActivatorNodeRef } : undefined}
      index={index}
      wrapperStyle={wrapperStyle({ index })}
      style={style({
        index,
        value: id,
        isDragging,
        isSorting,
        overIndex: over ? getIndex(over.id) : overIndex,
        containerId,
      })}
      transition={transition}
      transform={transform}
      fadeIn={mountedWhileDragging}
      listeners={listeners}
      renderItem={renderItem}
    />
  );
};

export default SortableItem;
